import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Plus, Trash2, Edit, Shield, Lock, Mail, KeyRound, Loader2, X, CheckCircle2,
} from "lucide-react";

import { API_URL } from "../../config";
import { useAuth } from "../../context/AuthContext";
import { ROLE_LABELS, isMasterLevel } from "../../lib/permissions";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const STORAGE_KEY = "bilhetez_admins_gate_at";
const TTL_MS = 30 * 60 * 1000;

const isGateUnlocked = () => {
  try {
    const ts = parseInt(sessionStorage.getItem(STORAGE_KEY) || "0", 10);
    return ts && Date.now() - ts < TTL_MS;
  } catch { return false; }
};
const markGateUnlocked = () => {
  try { sessionStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
};

const ROLES = [
  { value: "geral", label: "Geral (acesso total)", color: "text-rose-300" },
  { value: "master", label: "Master (acesso total)", color: "text-amber-300" },
  { value: "financeiro", label: "Financeiro (Dashboard + Finanças)", color: "text-emerald-300" },
  { value: "bilheteira", label: "Bilheteira (Tudo exceto Finanças e Configurações)", color: "text-violet-300" },
];

const RoleBadge = ({ role }) => {
  const map = {
    geral: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    master: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    financeiro: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    bilheteira: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[role] || "bg-zinc-700 text-zinc-300"}`}>
      {ROLE_LABELS[role] || role}
    </span>
  );
};

const PasswordGate = ({ open, onSuccess, onCancel, currentEmail }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pw) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/verify-password`, { password: pw }, { withCredentials: true });
      markGateUnlocked();
      toast.success("Acesso concedido a Admins (30 min)");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password incorreta");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md" data-testid="admins-gate">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-400" />
            Confirmar acesso a "Admins"
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {currentEmail ? (
              <>Insira a password de login de <strong className="text-amber-300">{currentEmail}</strong> para aceder a esta área.</>
            ) : (
              "Insira a sua password de login para ver/gerir os administradores."
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-zinc-300">Password</Label>
            <Input
              type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              autoFocus className="input-dark mt-2" data-testid="admins-gate-input"
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={busy || !pw} className="btn-primary" data-testid="admins-gate-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Desbloquear
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const emptyForm = () => ({
  email: "", name: "", role: "bilheteira", password: "", config_password: "",
});

export default function AdminsManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMaster = isMasterLevel(user?.role);

  const [unlocked, setUnlocked] = useState(isGateUnlocked());
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null); // admin obj or null
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/admins`, { withCredentials: true });
      setAdmins(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar admins");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (unlocked) fetchAdmins();
  }, [unlocked, fetchAdmins]);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error("Preencha email, nome e password");
      return;
    }
    if (form.role === "geral" || form.role === "master") {
      if (!form.config_password) {
        toast.error("Geral/Master requer também password de Configurações");
        return;
      }
    }
    try {
      await axios.post(`${API_URL}/api/admin/admins`, form, { withCredentials: true });
      toast.success("Admin criado");
      setCreateOpen(false);
      setForm(emptyForm());
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar admin");
    }
  };

  const handleUpdate = async (patch) => {
    if (!editing) return;
    try {
      await axios.put(`${API_URL}/api/admin/admins/${editing.id}`, patch, { withCredentials: true });
      toast.success("Admin atualizado");
      setEditing(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const handleToggleActive = async (a) => {
    try {
      await axios.put(`${API_URL}/api/admin/admins/${a.id}`, { active: !a.active }, { withCredentials: true });
      toast.success(!a.active ? "Admin reativado" : "Admin desativado");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao alterar estado");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API_URL}/api/admin/admins/${deleting.id}`, { withCredentials: true });
      toast.success("Admin eliminado");
      setDeleting(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao eliminar");
    }
  };

  if (!unlocked) {
    return (
      <PasswordGate
        open
        onSuccess={() => setUnlocked(true)}
        onCancel={() => navigate("/admin")}
        currentEmail={user?.email}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="admins-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestão de Admins</h1>
          <p className="text-zinc-500 text-sm mt-1">Gerir Administradores e Permissões</p>
        </div>
        {isMaster && (
          <Button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="admin-create-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Admin
          </Button>
        )}
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/10">
            <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Papel</th>
              <th className="px-4 py-3">Ativo</th>
              {isMaster && <th className="px-4 py-3 text-right">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">A carregar…</td></tr>
            )}
            {!loading && admins.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">Nenhum admin encontrado</td></tr>
            )}
            {admins.map((a) => (
              <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${a.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 text-white">
                    <Mail className="w-4 h-4 text-zinc-500" />
                    {a.email}
                    {a.id === user?.id && (
                      <span className="text-[10px] text-amber-300 ml-1">(tu)</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-300">{a.name}</td>
                <td className="px-4 py-3"><RoleBadge role={a.role} /></td>
                <td className="px-4 py-3">
                  {isMaster ? (
                    <Switch
                      checked={a.active !== false}
                      onCheckedChange={() => handleToggleActive(a)}
                      disabled={a.id === user?.id}
                      className="data-[state=checked]:bg-emerald-500"
                      data-testid={`admin-active-${a.id}`}
                    />
                  ) : (
                    a.active ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-red-400" />
                  )}
                </td>
                {isMaster && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm" variant="ghost" className="text-zinc-300 hover:text-white"
                        onClick={() => { setEditing(a); }}
                        data-testid={`admin-edit-${a.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                        onClick={() => setDeleting(a)}
                        disabled={a.id === user?.id}
                        data-testid={`admin-delete-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setForm(emptyForm()); }}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Criar Administrador</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Define o papel e a password inicial. O admin pode entrar imediatamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-300">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark mt-1.5" data-testid="admin-form-email" />
            </div>
            <div>
              <Label className="text-zinc-300">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark mt-1.5" data-testid="admin-form-name" />
            </div>
            <div>
              <Label className="text-zinc-300">Papel</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="input-dark mt-1.5" data-testid="admin-form-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value} className={r.color}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Password de Login</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark mt-1.5" data-testid="admin-form-password" />
            </div>
            {(form.role === "master" || form.role === "geral") && (
              <div>
                <Label className="text-zinc-300 flex items-center gap-1">
                  <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                  Password de Configurações (2ª password)
                </Label>
                <Input type="password" value={form.config_password} onChange={(e) => setForm({ ...form, config_password: e.target.value })} className="input-dark mt-1.5" data-testid="admin-form-config-password" />
                <p className="text-xs text-zinc-500 mt-1">Pedida ao entrar em "Configurações". Dura 30 minutos.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-white/10 text-zinc-300" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button className="btn-primary" onClick={handleCreate} data-testid="admin-form-save">Criar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Editar Administrador</DialogTitle>
          </DialogHeader>
          {editing && (
            <EditAdminForm editing={editing} onCancel={() => setEditing(null)} onSave={handleUpdate} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="bg-zinc-900 border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar administrador?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta ação é permanente. O admin <strong>{deleting?.email}</strong> deixa de poder iniciar sessão.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-zinc-300">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditAdminForm({ editing, onCancel, onSave }) {
  const [name, setName] = useState(editing.name || "");
  const [email, setEmail] = useState(editing.email || "");
  const [role, setRole] = useState(editing.role || "bilheteira");
  const [newPassword, setNewPassword] = useState("");
  const [newConfigPassword, setNewConfigPassword] = useState("");

  const save = () => {
    const patch = {};
    if (name !== editing.name) patch.name = name;
    if (email !== editing.email) patch.email = email;
    if (role !== editing.role) patch.role = role;
    if (newPassword) patch.new_password = newPassword;
    if ((role === "master" || role === "geral") && newConfigPassword) patch.new_config_password = newConfigPassword;
    if (Object.keys(patch).length === 0) { onCancel(); return; }
    onSave(patch);
  };

  return (
    <div className="space-y-3 mt-2">
      <div>
        <Label className="text-zinc-300">Nome</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="input-dark mt-1.5" data-testid="admin-edit-name" />
      </div>
      <div>
        <Label className="text-zinc-300">Email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark mt-1.5" data-testid="admin-edit-email" />
      </div>
      <div>
        <Label className="text-zinc-300">Papel</Label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="input-dark mt-1.5" data-testid="admin-edit-role"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            {ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value} className={r.color}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-zinc-300">Nova Password <span className="text-zinc-500 text-xs">(opcional)</span></Label>
        <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Deixe vazio para manter" className="input-dark mt-1.5" data-testid="admin-edit-password" />
      </div>
      {(role === "master" || role === "geral") && (
        <div>
          <Label className="text-zinc-300 flex items-center gap-1">
            <KeyRound className="w-3.5 h-3.5 text-amber-400" />
            Nova Password de Configurações <span className="text-zinc-500 text-xs">(opcional)</span>
          </Label>
          <Input type="password" value={newConfigPassword} onChange={(e) => setNewConfigPassword(e.target.value)} placeholder="Deixe vazio para manter" className="input-dark mt-1.5" data-testid="admin-edit-config-password" />
        </div>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" className="border-white/10 text-zinc-300" onClick={onCancel}>Cancelar</Button>
        <Button className="btn-primary" onClick={save} data-testid="admin-edit-save">Guardar</Button>
      </div>
    </div>
  );
}
